import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Chip } from "@scripts/react/components/Chip";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import {
    useCurrentModelChange,
    useCurrentModelMetrics,
    useFetchCurrentModelMetrics,
    useMetricsLoading,
    useMetricsError,
    useIsTraining,
    useModelWidgetState,
    useCurrentVersionLabel,
    useCurrentWorkflow,
    useRapidDataError,
    useSyncRapidDataFromWorkflow,
    useUser
} from "@/store";
import { useFlowState } from "@/hooks/useFlowState";
import { DeploySteps, Flows } from "@/types/flows";
import { MetricStat } from "./Steps/Model/components/MetricsComponents";
import { ModelWidgetState } from "./Steps/Model/types";
import Button from "@scripts/react/components/Button";

function formatTimeAgo(timestamp: any): string {
    if (!timestamp) return "";

    let date: Date;
    if (timestamp._seconds) {
        date = new Date(timestamp._seconds * 1000);
    } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
    } else {
        date = new Date(timestamp);
    }

    return moment(date).fromNow();
}

export function MetricsSnippet() {
    const navigate = useNavigate();
    const { computeUrlPath } = useFlowState();
    const currentChange = useCurrentModelChange();
    const currentMetrics = useCurrentModelMetrics();
    const metricsLoading = useMetricsLoading();
    const metricsError = useMetricsError();
    const fetchCurrentModelMetrics = useFetchCurrentModelMetrics();
    const workflow = useCurrentWorkflow();
    const rapidDataError = useRapidDataError();
    const syncRapidDataFromWorkflow = useSyncRapidDataFromWorkflow();
    const user = useUser();

    const isModelNotFound = !!(rapidDataError && workflow?.rapidDataId);
    const isTraining = useIsTraining();
    const widgetState = useModelWidgetState(user?.uid ?? "");
    const isUnseenChange = widgetState === ModelWidgetState.UNSEEN_CHANGE;
    const versionLabel = useCurrentVersionLabel();

    const timeAgo = useMemo(() => {
        if (!currentChange?.timestamp) return "";
        return formatTimeAgo(currentChange.timestamp);
    }, [currentChange?.timestamp]);

    useEffect(() => {
        if (currentChange && !currentMetrics && !metricsLoading && !metricsError) {
            fetchCurrentModelMetrics();
        }
    }, [currentChange, currentMetrics, metricsLoading, metricsError, fetchCurrentModelMetrics]);

    const handleClick = useCallback(() => {
        const modelPath = computeUrlPath({ flow: Flows.DEPLOY, step: DeploySteps.MODEL });
        navigate(modelPath);
    }, [navigate, computeUrlPath]);

    const hasAnyMetrics =
        currentMetrics &&
        (currentMetrics.map !== undefined ||
            currentMetrics.precision !== undefined ||
            currentMetrics.recall !== undefined);

    const displayError = isModelNotFound ? rapidDataError : metricsError;
    const showErrorContent = isModelNotFound || !!metricsError;

    const variantStyles = useMemo(() => {
        if (isTraining) {
            return {
                color: "gray" as const,
                container: "border-border focus:ring-gray-500/50 hover:border-gray-400",
                header: "border-border bg-gray-50 group-hover:border-gray-400",
                content: "bg-gray-50",
                chip: {
                    text: "Retraining",
                    icon: "fa-spinner fa-spin",
                    color: "gray",
                    className: "!bg-gray-200/60 !text-secondary"
                }
            };
        }

        if (isUnseenChange) {
            return {
                color: "purple" as const,
                container: "border-purple-200 focus:ring-purple-500/50 hover:border-purple-300",
                header: "group-hover:border-purple-300 border-purple-200 bg-purple-50 ",
                content: "bg-purple-50",
                chip:
                    timeAgo && !isModelNotFound
                        ? { text: `Updated ${timeAgo}`, icon: "fa-arrow-right", color: "purple" }
                        : null
            };
        }

        return {
            color: "gray" as const,
            container: "border-border focus:ring-gray-500/50 hover:border-gray-400",
            header: "border-border bg-gray-50 group-hover:border-gray-400",
            content: "bg-gray-50",
            chip: null
        };
    }, [isTraining, isUnseenChange, timeAgo, isModelNotFound]);

    if (!currentChange && !isModelNotFound) {
        return null;
    }

    return (
        <button
            onClick={handleClick}
            className={twClassNames(
                "group mb-2 flex w-full flex-col overflow-hidden rounded-lg border font-sans transition-all hover:shadow-sm focus:outline-none focus:ring-2",
                variantStyles.container
            )}
            title="View model metrics"
        >
            <div
                className={twClassNames(
                    "flex items-center justify-between gap-2 border-b p-3 pl-4 text-base text-primary",
                    variantStyles.header
                )}
            >
                <span className="shrink-0 font-semibold">
                    {isModelNotFound
                        ? "Model not found"
                        : versionLabel
                          ? `${versionLabel} Metrics`
                          : "Metrics"}
                </span>
                {variantStyles.chip ? (
                    <Chip
                        color={variantStyles.chip.color}
                        type="text"
                        className={twClassNames("min-w-0 shrink", variantStyles.chip.className)}
                    >
                        {isTraining && <i className="far fa-spinner fa-spin shrink-0" />}
                        <span className="truncate">{variantStyles.chip.text}</span>
                        {!isTraining && <i className={`far ${variantStyles.chip.icon} shrink-0`} />}
                    </Chip>
                ) : (
                    <i className="far fa-fw fa-arrow-right text-sm text-tertiary" />
                )}
            </div>

            <div
                className={twClassNames(
                    "flex items-end justify-around gap-4 p-4",
                    showErrorContent ? "bg-white" : variantStyles.content,
                    (!hasAnyMetrics && !metricsLoading) || showErrorContent ? "min-h-[80px]" : ""
                )}
            >
                {metricsLoading && !isModelNotFound ? (
                    <>
                        <MetricStat
                            label="Overall"
                            value={undefined}
                            isLoading={true}
                            color={variantStyles.color}
                        />
                        <MetricStat
                            label="Accuracy"
                            value={undefined}
                            isLoading={true}
                            color={variantStyles.color}
                        />
                        <MetricStat
                            label="Coverage"
                            value={undefined}
                            isLoading={true}
                            color={variantStyles.color}
                        />
                    </>
                ) : showErrorContent ? (
                    <div
                        className="flex h-full w-full flex-col items-center gap-2"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <span className="text-xs font-medium text-tertiary">
                            <i className="far fa-triangle-exclamation" /> Error: {displayError}
                        </span>
                        <Button
                            type="secondary"
                            size="xxsmall"
                            onClick={(e) => {
                                e.stopPropagation();
                                isModelNotFound
                                    ? syncRapidDataFromWorkflow(workflow?.rapidDataId ?? null)
                                    : fetchCurrentModelMetrics();
                            }}
                        >
                            <i className="far fa-rotate-right" />
                            Retry
                        </Button>
                    </div>
                ) : hasAnyMetrics ? (
                    <>
                        <MetricStat
                            label="Overall"
                            value={currentMetrics?.map}
                            color={variantStyles.color}
                        />
                        <MetricStat
                            label="Accuracy"
                            value={currentMetrics?.precision}
                            color={variantStyles.color}
                        />
                        <MetricStat
                            label="Coverage"
                            value={currentMetrics?.recall}
                            color={variantStyles.color}
                        />
                    </>
                ) : (
                    <div className="flex w-full items-center justify-center">
                        <span className="text-sm text-tertiary">No metrics available yet</span>
                    </div>
                )}
            </div>
        </button>
    );
}
